import { delay } from 'redux-saga';
import {
  all,
  call,
  put,
  select,
  spawn,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { PEM_BRAND_LOCATOR } from 'constants/all';

import { getTechnicalDataPackage } from 'modules/viewerPage/data/reducer';

import {
  distributors,
  preferredDistributors,
  partPurchasedComponentSearch,
} from 'services/productService';

import {
  fastenerBom,
  fetchAvailableDistributors,
  selectedDistributors,
  types,
  unresolvedFastenerBom,
} from './actions';
import { PRODUCT_MATCH_CRITERIA } from './constants';
import {
  getBomItemByProductId,
  getIsProductInBom,
  getPreferredDistributorIds,
  getSelectedDistributorIds,
  getSelectedUnresolvedBomItem,
} from './reducer';

export default function* root() {
  yield takeEvery(types.FASTENER_BOM_ADD_ITEM_REQUEST, watchAddBomItem);
  yield takeLatest(
    types.FETCH_AVAILABLE_DISTRIBUTORS_REQUEST,
    watchFetchAvailableDistributors,
  );
  yield takeLatest(
    [
      types.SELECTED_DISTRIBUTORS_ADD,
      types.SELECTED_DISTRIBUTORS_REMOVE,
      types.SELECTED_DISTRIBUTORS_SET,
    ],
    watchPersistPreferredDistributors,
  );
  yield takeLatest(types.RESOLVE_MATCH_SUCCESS, watchResolveMatchSuccess);
}

function* watchAddBomItem(action) {
  // If the product already exists in the BOM, merge the two with a quantity that is their sum
  try {
    const { bomItem } = action.payload;
    const isInBom = yield select(getIsProductInBom, bomItem?.product?.id);
    if (isInBom) {
      const existingBomItem = yield select(
        getBomItemByProductId,
        bomItem?.product?.id,
      );
      yield put(
        fastenerBom.addItemSuccess({
          bomItem: {
            ...bomItem,
            quantity:
              (existingBomItem?.quantity ?? 0) + (bomItem?.quantity ?? 0),
          },
        }),
      );
    } else {
      yield put(fastenerBom.addItemSuccess({ bomItem }));
    }
  } catch (error) {
    console.error('Something went wrong when trying to add a BOM item', error);
  }
}

function* watchFetchAvailableDistributors() {
  try {
    const response = yield call(distributors.list);
    yield put(fetchAvailableDistributors.success({ distributors: response }));
  } catch (error) {
    yield put(fetchAvailableDistributors.error());
    console.error(
      'An error occured when trying to fetch available distributors',
      error,
    );
  }
}

function* watchPersistPreferredDistributors() {
  try {
    yield call(delay, 1500);
    const preferredDistributorIds = yield select(getPreferredDistributorIds);
    const distributorIds = yield select(getSelectedDistributorIds);
    if (!preferredDistributorIds.length) {
      yield call(preferredDistributors.update, { distributorIds });
    }
  } catch (error) {
    console.error(
      'Error occured when persisting preferred distributors',
      error,
    );
  }
}

function* fetchPreferredDistributors() {
  try {
    const response = yield call(preferredDistributors.list);
    const distributorId = response.map(({ id }) => id);
    yield put(selectedDistributors.setInitial({ distributorId }));
  } catch (error) {
    console.error(
      'An error occured when trying to fetch available distributors',
      error,
    );
  }
}

// TODO: refactor this to be invoked from an initialize action
export function* initializeFastenerAvailabilityApp() {
  try {
    yield all([
      put(fetchAvailableDistributors.request()),
      spawn(fetchPreferredDistributors),
    ]);

    // This is a hack to pre-populate a BOM
    const technicalDataPackage = yield select(getTechnicalDataPackage);
    const parts = technicalDataPackage?.partInformation || [];

    // Make API call to search endpoint
    const BATCH_SIZE = 10;
    const partUuids = parts.map((p) => p.uuid);
    const partUuidSegments = [];
    while (partUuids.length > 0) {
      if (partUuids.length >= BATCH_SIZE) {
        partUuidSegments.push(partUuids.slice(0, BATCH_SIZE));
        partUuids.splice(0, BATCH_SIZE);
      } else {
        partUuidSegments.push(partUuids);
        break;
      }
    }
    const responses = yield all(
      partUuidSegments.map((seg) =>
        call(
          partPurchasedComponentSearch.post,
          technicalDataPackage.rootUuid,
          {
            partUuids: seg,
          },
          [PEM_BRAND_LOCATOR],
        ),
      ),
    );
    const allResults = responses.map((r) => r.results).flatMap((x) => x);

    if (allResults.length) {
      const nodes = technicalDataPackage.partInformation;
      const initialFullBom = allResults
        .filter(({ count }) => count > 0)
        .map(({ partUuid, products }) => {
          const part = nodes.find((n) => n.uuid === partUuid);

          let forceDrawer = false;

          if (
            products.length === 1 &&
            products[0].matchCriteria.includes(
              PRODUCT_MATCH_CRITERIA.PRODUCT_LINKED_IN_BAQ,
            )
          ) {
            return {
              needsResolution: false,
              products,
              partUuid,
              quantity: part.quantity,
            };
          }

          const anyExact = products.some((p) =>
            p.matchCriteria.includes(PRODUCT_MATCH_CRITERIA.PRODUCT_SKU_EXACT),
          );
          const anyFuzzy = products.some((p) =>
            p.matchCriteria.includes(PRODUCT_MATCH_CRITERIA.PRODUCT_SKU_FUZZY),
          );
          const trimmedProducts = products.filter(
            (p) =>
              (anyExact &&
                p.matchCriteria.includes(
                  PRODUCT_MATCH_CRITERIA.PRODUCT_SKU_EXACT,
                )) ||
              (!anyExact &&
                anyFuzzy &&
                p.matchCriteria.includes(
                  PRODUCT_MATCH_CRITERIA.PRODUCT_SKU_FUZZY,
                )) ||
              (!anyExact && !anyFuzzy),
          );

          if (!anyExact && !anyFuzzy) {
            // If only geometric matches are left, show the drawer even if there is just one match, since
            //  a geometric match is much less certain than a part number match
            forceDrawer = true;
          }

          return {
            needsResolution: trimmedProducts.length > 1 || forceDrawer,
            products: trimmedProducts,
            partUuid,
            quantity: part.quantity,
          };
        });

      const initialUnresolvedBom = initialFullBom.filter(
        ({ needsResolution }) => needsResolution,
      );
      const initialResolvedBom = Object.values(
        initialFullBom
          .filter(({ needsResolution }) => !needsResolution)
          .map(({ products, needsResolution, quantity }) => {
            return {
              ...products[0],
              needsResolution,
              quantity,
            };
          })
          .reduce((soFar, next) => {
            if (next.product?.id in soFar) {
              soFar[next.product?.id].quantity += next.quantity;
            } else {
              soFar[next.product?.id] = next;
            }
            return soFar;
          }, {}),
      );

      yield put(fastenerBom.set({ bom: initialResolvedBom }));
      yield put(unresolvedFastenerBom.set({ bom: initialUnresolvedBom }));
    }
  } catch (error) {
    console.error(
      'An error occurred when initializing the Fastener Availability App',
      error,
    );
  }
}

function* watchResolveMatchSuccess({ payload: { product } }) {
  try {
    const existingBomItem = yield select(getBomItemByProductId, product?.id);
    const unresolvedBomItem = yield select(getSelectedUnresolvedBomItem);
    const resolvedBomItem = {
      ...unresolvedBomItem,
      quantity:
        unresolvedBomItem.quantity +
        (existingBomItem ? existingBomItem.quantity : 0),
      product: product,
    };
    yield put(fastenerBom.addItemSuccess({ bomItem: resolvedBomItem }));
  } catch (error) {
    console.error('Error occurred when trying to resolve match', error);
  }
}
